import dayjs from 'dayjs'
import { usePathname } from 'expo-router'
import { useCallback, useEffect, useMemo, useRef, useState, type PropsWithChildren } from 'react'
import { Platform, View } from 'react-native'
import { MMKV } from 'react-native-mmkv'
import { useSpinDelay } from 'spin-delay'
import type z from 'zod'
import { createJSONStorage, persist as persistMiddleware } from 'zustand/middleware'
import { useStore } from 'zustand/react'
import { createStore, type StoreApi } from 'zustand/vanilla'
/* eslint-disable react-hooks/refs */
import FlowHeader from '~/components/FlowHeader'
import FlowNavigation from '~/components/FlowNavigation.tsx'
import { FlowContext } from '~/components/flows/useFlowManager'
import ContentBox from '~/components/layout/ContentBox.tsx'
import { useIsBottomSheetOpen } from '~/components/ui/bottom-sheet'
import { type ButtonVariant } from '~/components/ui/button'
import { FlowTracker } from '~/lib/analytics/flowEvents'
import { usePostHog } from '~/lib/analytics/posthog'
import { cn } from '~/lib/utils.ts'

export type PhaseData = Record<string, unknown>
export type FlowData = Record<string, PhaseData>

function createMMKVStorage() {
  const mmkv = new MMKV({ id: 'flow-storage' })

  return {
    getItem: (name: string) => mmkv.getString(name) ?? null,
    setItem: (name: string, value: boolean | string | number | ArrayBuffer) => {
      mmkv.set(name, value)
    },
    removeItem: (name: string) => {
      mmkv.delete(name)
    },
  }
}

const storage =
  Platform.OS === 'web'
    ? createJSONStorage(() => localStorage)
    : createJSONStorage(() => createMMKVStorage())

function deepMerge<T extends Record<string, unknown>>(target: T, source: Partial<T>): T {
  const result = { ...target }

  for (const key in source) {
    const sourceValue = source[key]
    const targetValue = result[key]

    if (
      sourceValue &&
      typeof sourceValue === 'object' &&
      !Array.isArray(sourceValue) &&
      targetValue &&
      typeof targetValue === 'object' &&
      !Array.isArray(targetValue)
    ) {
      result[key] = deepMerge(
        targetValue as Record<string, unknown>,
        sourceValue as Record<string, unknown>
      ) as T[Extract<keyof T, string>]
    } else if (sourceValue !== undefined) {
      result[key] = sourceValue as T[Extract<keyof T, string>]
    }
  }

  return result
}

export interface FlowPhase<
  TPhaseId extends string = string,
  TPhaseData extends PhaseData = PhaseData,
> {
  id: TPhaseId
  title: string
  schema?: z.ZodType<TPhaseData>
  data?: TPhaseData
  dataKey?: keyof TPhaseData
  isComplete?: boolean
  condition?: (data: FlowData) => boolean
  hidden?: boolean
  usesCustomButtons?: boolean | ((data: FlowData) => boolean)
  hiddenFromProgress?: boolean
}

export type PhaseIds<TPhases extends readonly FlowPhase[]> = TPhases[number]['id']

export function createFlowPhases<const TPhases extends readonly FlowPhase[]>(
  phases: TPhases
): TPhases {
  return phases
}

export interface FlowConfig<TPhases extends readonly FlowPhase[]> {
  flowId: string
  persist?: boolean
  phases: TPhases
  initialPhaseId?: PhaseIds<TPhases>
  initialData?: FlowData
  onClose?: (resetFlow: () => void) => void
  onComplete?: (data: FlowData) => Promise<boolean>
  flowTitle?: string
  contentBoxVariant?: React.ComponentProps<typeof ContentBox>['variant']
  onNext?: (
    data: FlowData,
    currentPhaseId: PhaseIds<TPhases>,
    nextPhaseId: PhaseIds<TPhases> | null,
    updateData: (
      phaseId: PhaseIds<TPhases>,
      data: FlowData[PhaseIds<TPhases>],
      isUserUpdate?: boolean
    ) => void,
    visitedPhases: Set<PhaseIds<TPhases>>
  ) => Promise<boolean>
  onPrevious?: (
    data: FlowData,
    currentPhaseId: PhaseIds<TPhases>,
    updateData: (
      phaseId: PhaseIds<TPhases>,
      data: FlowData[PhaseIds<TPhases>],
      isUserUpdate?: boolean
    ) => void,
    visitedPhases: Set<PhaseIds<TPhases>>
  ) => Promise<boolean>
  isNextDisabled?: (data: FlowData, currentPhaseId: PhaseIds<TPhases>) => boolean
  failureResetPhase?: PhaseIds<TPhases>
}

export interface FlowManager<TPhases extends readonly FlowPhase[] = readonly FlowPhase[]> {
  data: FlowData
  phases: TPhases[number][]
  currentPhase: TPhases[number]
  currentPhaseId: PhaseIds<TPhases>
  currentPhaseIndex: number
  isFirstPhase: boolean
  isLastPhase: boolean
  nextPhase: () => void
  previousPhase: () => void
  goToPhase: (phaseId: PhaseIds<TPhases>) => void
  updateData: <K extends PhaseIds<TPhases>>(
    phaseId: K,
    data: FlowData[K] | ((current: FlowData[K]) => FlowData[K]) | undefined
  ) => void
  completeFlow: () => Promise<boolean>
  resetFlow: () => Promise<void>
  canProceed: boolean
  onClose: () => void
  canGoBack: boolean
  setNextPhaseAction: (action: NextPhaseAction<TPhases>) => void
  isLoading: boolean
  setIsLoading: (isLoading: boolean) => void
  recalculateCanProceed: () => void
}

type NextPhaseAction<TPhases extends readonly FlowPhase[]> = (
  data: FlowData,
  updateData: FlowManager<TPhases>['updateData']
) => Promise<boolean>

type ButtonConfigProp<TPhases extends readonly FlowPhase[], R = string> =
  | R
  | ((currentPhaseId: PhaseIds<TPhases>, currentPhaseIndex: number, flowData: FlowData) => R)

export function FlowProvider<TPhases extends readonly FlowPhase[] = readonly FlowPhase[]>({
  children,
  flowId,
  phases,
  initialPhaseId,
  initialData,
  footer,
  className,
  onComplete,
  onClose,
  flowTitle,
  isNextDisabled,
  onNext,
  onPrevious,
  nextLabel,
  nextButtonVariant,
  previousLabel,
  completeLabel,
  isLoading: isLoadingProp,
  persist = true,
  contentBoxVariant,
  contentClassName,
  headerClassName,
  isInBottomSheet,
  failureResetPhase,
  showCloseButton = true,
  showResetButton = true,
}: PropsWithChildren<
  FlowConfig<TPhases> & {
    footer?: React.ReactNode
    className?: string
    nextLabel?: ButtonConfigProp<TPhases>
    nextButtonVariant?: ButtonConfigProp<TPhases, ButtonVariant>
    previousLabel?: ButtonConfigProp<TPhases>
    completeLabel?: ButtonConfigProp<TPhases>
    isLoading?: boolean
    contentClassName?: string
    headerClassName?: string
    isInBottomSheet?: boolean
    showCloseButton?: boolean
    showResetButton?: boolean
  }
>) {
  type TData = FlowData
  const visitedPhases = useRef<Set<string>>(new Set())
  const [isExited, setIsExited] = useState(false)
  const [canProceedVersion, setCanProceedVersion] = useState(0)

  const posthog = usePostHog()
  const pathname = usePathname()

  const context = isInBottomSheet ? 'bottom_sheet' : 'route'
  const route = !isInBottomSheet ? pathname : undefined

  const trackerRef = useRef<FlowTracker | null>(null)
  const lastPhaseRef = useRef<{
    id: string
    index: number
    title?: string
  } | null>(null)

  const tracker = useMemo(() => {
    if (!trackerRef.current) {
      trackerRef.current = new FlowTracker({
        flowId,
        flowTitle,
        context,
        route,
        posthog,
      })
    }
    return trackerRef.current
  }, [flowId, flowTitle, context, route, posthog])

  const createInitialFlowData = useCallback(() => {
    const data: Record<string, PhaseData> = {}

    phases.forEach((phase) => {
      if (phase.data !== undefined) {
        data[phase.id] = phase.data
      }
    })

    if (initialData) {
      return deepMerge(data, initialData)
    }

    return data
  }, [phases, initialData])

  const date = dayjs().format('YYYY-MM-DD')
  const storageKey = `flow_${flowId}_${date}`

  type FlowStoreState = {
    data: FlowData
    currentPhaseId: string
    isLoading: boolean
  }

  type FlowStoreActions = {
    setData: (data: FlowData | ((prev: FlowData) => FlowData)) => void
    setCurrentPhaseId: (phaseId: string) => void
    setIsLoading: (isLoading: boolean) => void
    reset: () => void
  }

  type FlowStore = FlowStoreState & FlowStoreActions

  const storeRef = useRef<StoreApi<FlowStore> | null>(null)

  if (!storeRef.current) {
    const initialFlowData = createInitialFlowData()
    const firstEnabledPhaseId = phases.find((p) => !p.hidden)?.id || phases[0]?.id

    storeRef.current = createStore<FlowStore>()(
      persistMiddleware(
        (set) => ({
          data: initialFlowData,
          currentPhaseId: initialPhaseId || firstEnabledPhaseId || '',
          isLoading: isLoadingProp ?? false,
          setData: (updater: FlowData | ((prev: FlowData) => FlowData)) => {
            set((state) => ({
              data: typeof updater === 'function' ? updater(state.data) : updater,
            }))
          },
          setCurrentPhaseId: (phaseId: string) => {
            set({ currentPhaseId: phaseId })
          },
          setIsLoading: (loading: boolean) => {
            set({ isLoading: loading })
          },
          reset: () => {
            set({
              data: createInitialFlowData(),
              currentPhaseId: initialPhaseId || firstEnabledPhaseId || '',
            })
          },
        }),
        {
          name: storageKey,
          storage,
          partialize: (state: FlowStore) => ({
            data: state.data,
            currentPhaseId: state.currentPhaseId,
            isLoading: false,
          }),
          merge: (persistedState: unknown, currentState: FlowStore) => {
            const persisted = persistedState as Partial<FlowStoreState>
            const mergedData = deepMerge(currentState.data, persisted.data || {})
            const mergedCurrentPhaseId = persisted.currentPhaseId ?? currentState.currentPhaseId

            return {
              ...currentState,
              data: mergedData,
              currentPhaseId: mergedCurrentPhaseId,
            } as FlowStore
          },
          skipHydration: !persist,
        }
      )
    )
  }

  const store = storeRef.current!

  useEffect(() => {
    if (isLoadingProp !== undefined) {
      store.getState().setIsLoading(isLoadingProp)
    }
  }, [isLoadingProp, store])

  const data = useStore(store, (state: FlowStore) => state.data)
  const currentPhaseId = useStore(store, (state: FlowStore) => state.currentPhaseId)
  const isLoading = useStore(store, (state: FlowStore) => state.isLoading)

  const getEnabledPhases = useCallback(
    (phases: TPhases, flowData: TData, countForProgress?: boolean) =>
      phases.filter((phase) => {
        if (phase.hidden) {
          return false
        }

        return (
          (phase.condition?.(flowData) ?? true) && (!countForProgress || !phase.hiddenFromProgress)
        )
      }),
    []
  )

  const enabledPhases = useMemo(
    () => getEnabledPhases(phases, data as TData),
    [phases, data, getEnabledPhases]
  )

  const enabledPhasesForProgress = useMemo(
    () => getEnabledPhases(phases, data as TData, true),
    [phases, data, getEnabledPhases]
  )

  const currentPhaseIndex = useMemo(() => {
    const index = enabledPhases.findIndex((p) => p.id === currentPhaseId)
    return index === -1 ? 0 : index
  }, [enabledPhases, currentPhaseId])

  const currentPhase = useMemo(() => {
    return enabledPhases.find((p) => p.id === currentPhaseId) ?? enabledPhases[0]
  }, [enabledPhases, currentPhaseId])

  const isFirstPhase = currentPhaseIndex === 0
  const isLastPhase = currentPhaseIndex >= enabledPhases.length - 1
  const isLastPhaseForProgress = currentPhaseIndex >= enabledPhasesForProgress.length - 1

  const resetFlow = useCallback(async () => {
    const currentData = store.getState().data
    const initialPhaseIdValue =
      getEnabledPhases(phases, currentData as TData)[0]?.id || phases[0]?.id

    if (tracker && lastPhaseRef.current) {
      tracker.recordReset(initialPhaseIdValue)
    }

    store.getState().reset()

    if (persist) {
      const persistApi = (store as unknown as { persist?: { clearStorage: () => Promise<void> } })
        .persist
      if (persistApi) {
        await persistApi.clearStorage()
      }
    }

    store.getState().setCurrentPhaseId(initialPhaseIdValue)
    visitedPhases.current = new Set([initialPhaseIdValue])
    lastPhaseRef.current = null
  }, [getEnabledPhases, phases, tracker, store, persist])

  const canProceed = useMemo(() => {
    void canProceedVersion

    const isDisabled = isNextDisabled?.(data, currentPhaseId)

    if (typeof isDisabled === 'boolean') {
      return !isDisabled
    }

    const phase = phases.find((p) => p.id === currentPhaseId)

    const usesCustomButtonsValue =
      typeof phase?.usesCustomButtons === 'function'
        ? phase.usesCustomButtons(data)
        : (phase?.usesCustomButtons ?? false)

    if (usesCustomButtonsValue) {
      return true
    }

    const schema = phases.find((p) => p.id === currentPhaseId)?.schema

    if (!schema) {
      return true
    }

    const currentPhaseData =
      data?.[phase?.dataKey as keyof TData] ?? data?.[currentPhaseId as keyof TData]

    if (!currentPhaseData) {
      return false
    }

    const result = schema.safeParse(currentPhaseData)
    return result.success
  }, [phases, isNextDisabled, currentPhaseId, data, canProceedVersion])

  const [visitedCount, setVisitedCount] = useState(1)

  useEffect(() => {
    setVisitedCount(visitedPhases.current.size)
  }, [currentPhaseId])

  const canGoBack = visitedCount > 1

  const goToPhase = useCallback(
    (phaseId: string) => {
      const currentState = store.getState()
      const currentData = currentState.data
      const currentEnabledPhases = getEnabledPhases(phases, currentData as TData)
      if (currentEnabledPhases.some((p) => p.id === phaseId)) {
        visitedPhases.current.add(phaseId)
        currentState.setCurrentPhaseId(phaseId)
      }
    },
    [store, getEnabledPhases, phases]
  )

  const completeFlow = useCallback(async () => {
    store.getState().setIsLoading(true)
    let isSuccess = true

    if (onComplete) {
      const currentData = store.getState().data
      isSuccess = await onComplete(currentData)
    }

    if (tracker && lastPhaseRef.current) {
      tracker.recordComplete(isSuccess)
    }

    if (isSuccess) {
      const currentData = store.getState().data
      const currentEnabledPhases = getEnabledPhases(phases, currentData as TData)
      if (tracker) {
        tracker.reset(initialPhaseId || currentEnabledPhases[0]?.id || phases[0]?.id)
      }
      lastPhaseRef.current = null
      await resetFlow()
      setIsExited(true)
    } else if (failureResetPhase) {
      goToPhase(failureResetPhase)
    }

    store.getState().setIsLoading(false)
    return isSuccess
  }, [
    onComplete,
    resetFlow,
    failureResetPhase,
    goToPhase,
    initialPhaseId,
    phases,
    tracker,
    store,
    getEnabledPhases,
  ])

  const updateData = useCallback(
    // oxlint-disable-next-line typescript/no-redundant-type-constituents
    (phaseId: string, updater: unknown | ((currentData: unknown) => unknown)) => {
      store.getState().setData((current: FlowData) => {
        const currentPhaseData = current?.[phaseId]
        const newData = typeof updater === 'function' ? updater(currentPhaseData) : updater

        return {
          ...current,
          [phaseId]: newData,
        }
      })
    },
    [store]
  )

  const nextPhaseAction = useRef<NextPhaseAction<TPhases>>(() => Promise.resolve(true))

  const goToNextPhase = useCallback(async () => {
    const currentState = store.getState()
    const currentData = currentState.data
    const currentPhaseIdValue = currentState.currentPhaseId
    const currentEnabledPhases = getEnabledPhases(phases, currentData as TData)
    const currentPhaseIndexValue = currentEnabledPhases.findIndex(
      (p) => p.id === currentPhaseIdValue
    )
    const isLastPhaseValue = currentPhaseIndexValue >= currentEnabledPhases.length - 1

    const isDisabled = isNextDisabled?.(currentData, currentPhaseIdValue)
    const canProceedValue =
      typeof isDisabled === 'boolean'
        ? !isDisabled
        : (() => {
            const phase = phases.find((p) => p.id === currentPhaseIdValue)
            const usesCustomButtonsValue =
              typeof phase?.usesCustomButtons === 'function'
                ? phase.usesCustomButtons(currentData)
                : (phase?.usesCustomButtons ?? false)

            if (usesCustomButtonsValue) {
              return true
            }

            const schema = phase?.schema

            if (!schema) {
              return true
            }

            const currentPhaseData =
              currentData?.[phase?.dataKey as keyof TData] ??
              currentData?.[currentPhaseIdValue as keyof TData]

            if (!currentPhaseData) {
              return false
            }

            const result = schema.safeParse(currentPhaseData)
            return result.success
          })()

    if (!canProceedValue) {
      return
    }

    currentState.setIsLoading(true)

    if (!(await nextPhaseAction.current(currentData, updateData))) {
      return currentState.setIsLoading(false)
    }

    const nextIndex = currentPhaseIndexValue + 1
    const nextPhaseId = currentEnabledPhases[nextIndex]?.id ?? null

    if (
      onNext &&
      !(await onNext(
        currentData,
        currentPhaseIdValue,
        nextPhaseId,
        updateData,
        visitedPhases.current
      ))
    ) {
      return currentState.setIsLoading(false)
    }

    if (!isLastPhaseValue && nextIndex < currentEnabledPhases.length && nextPhaseId) {
      visitedPhases.current.add(nextPhaseId)
      currentState.setCurrentPhaseId(nextPhaseId)

      const nextPhase = currentEnabledPhases[nextIndex]

      if (tracker && nextPhase) {
        tracker.recordPhaseView(nextPhaseId)
        lastPhaseRef.current = {
          id: nextPhaseId,
          index: nextIndex,
          title: nextPhase.title,
        }
      }
    } else {
      await completeFlow()
    }

    currentState.setIsLoading(false)
  }, [store, updateData, onNext, getEnabledPhases, phases, completeFlow, tracker, isNextDisabled])

  const goToPreviousPhase = useCallback(async () => {
    const currentState = store.getState()
    const currentData = currentState.data
    const currentPhaseIdValue = currentState.currentPhaseId
    const currentEnabledPhases = getEnabledPhases(phases, currentData as TData)
    const currentPhaseIndexValue = currentEnabledPhases.findIndex(
      (p) => p.id === currentPhaseIdValue
    )
    const isFirstPhaseValue = currentPhaseIndexValue === 0

    if (!isFirstPhaseValue) {
      const prevIndex = currentPhaseIndexValue - 1

      if (prevIndex >= 0) {
        const prevPhaseId = currentEnabledPhases[prevIndex].id
        currentState.setCurrentPhaseId(prevPhaseId)

        const prevPhase = currentEnabledPhases[prevIndex]

        if (tracker && prevPhase) {
          tracker.recordPhaseView(prevPhaseId)
          lastPhaseRef.current = {
            id: prevPhaseId,
            index: prevIndex,
            title: prevPhase.title,
          }
        }
      }
    } else {
      currentState.setIsLoading(true)

      if (
        onPrevious &&
        !(await onPrevious(currentData, currentPhaseIdValue, updateData, visitedPhases.current))
      ) {
        return currentState.setIsLoading(false)
      }

      if (tracker && lastPhaseRef.current) {
        tracker.recordExit('back_from_first')
      }

      await resetFlow()
      onClose?.(resetFlow)
      currentState.setIsLoading(false)
    }
  }, [store, getEnabledPhases, phases, onClose, resetFlow, onPrevious, updateData, tracker])

  const setNextPhaseAction = useCallback((action: NextPhaseAction<TPhases>) => {
    nextPhaseAction.current = async (
      data: FlowData,
      updateData: (
        phaseId: string,
        data: PhaseData | ((current: PhaseData) => PhaseData) | undefined
      ) => void
    ) => {
      const result = await action(data, updateData)
      nextPhaseAction.current = () => Promise.resolve(true)
      return result
    }
  }, [])

  useEffect(() => {
    if (!currentPhase || !tracker) {
      return
    }

    const phaseId = currentPhase.id
    const hasPhaseChanged = !lastPhaseRef.current || lastPhaseRef.current.id !== phaseId

    if (!hasPhaseChanged) {
      return
    }

    if (!lastPhaseRef.current) {
      tracker.recordStart(phaseId)
    }

    tracker.recordPhaseView(phaseId)

    lastPhaseRef.current = {
      id: phaseId,
      index: currentPhaseIndex,
      title: currentPhase.title,
    }
  }, [currentPhase, currentPhaseId, currentPhaseIndex, tracker])

  useEffect(() => {
    const currentState = store.getState()
    const currentData = currentState.data
    const currentPhaseIdValue = currentState.currentPhaseId
    const currentIsLoading = currentState.isLoading
    const currentEnabledPhases = getEnabledPhases(phases, currentData as TData)
    const isValid = currentEnabledPhases.some((p) => p.id === currentPhaseIdValue)

    if (!isValid && !currentIsLoading) {
      const nextId = currentEnabledPhases[0]?.id || phases[0]?.id

      if (nextId && nextId !== currentPhaseIdValue) {
        currentState.setCurrentPhaseId(nextId)
      }
    }
  }, [phases, data, currentPhaseId, store, getEnabledPhases])

  useEffect(() => {
    if (currentPhaseId) {
      visitedPhases.current.add(currentPhaseId)
    }
  }, [currentPhaseId])

  const displayIsLoading = useSpinDelay(isLoading, {
    delay: 200,
    minDuration: 700,
  })

  const completeLabelText =
    typeof completeLabel === 'function'
      ? completeLabel(currentPhaseId, currentPhaseIndex, data)
      : completeLabel

  const nextLabelText =
    typeof nextLabel === 'function' ? nextLabel(currentPhaseId, currentPhaseIndex, data) : nextLabel

  const nextButtonVariantValue =
    (typeof nextButtonVariant === 'function'
      ? nextButtonVariant(currentPhaseId, currentPhaseIndex, data)
      : // eslint-disable-next-line i18next/no-literal-string
        nextButtonVariant) ?? 'primary'

  const previousLabelText =
    typeof previousLabel === 'function'
      ? previousLabel(currentPhaseId, currentPhaseIndex, data)
      : previousLabel

  const isBottomSheetOpen = useIsBottomSheetOpen()
  const isInactive = !isInBottomSheet && isBottomSheetOpen

  const usesCustomButtons =
    typeof currentPhase?.usesCustomButtons === 'function'
      ? currentPhase.usesCustomButtons(data)
      : (currentPhase?.usesCustomButtons ?? false)

  const handleClose = useCallback(() => {
    store.getState().setIsLoading(false)

    if (tracker && lastPhaseRef.current) {
      // eslint-disable-next-line i18next/no-literal-string
      tracker.recordExit('close')
    }

    return onClose?.(resetFlow)
  }, [store, onClose, resetFlow, tracker])

  const recalculateCanProceed = useCallback(() => {
    setCanProceedVersion((prev) => prev + 1)
  }, [])

  const setIsLoadingWrapper = useCallback(
    (loading: boolean) => {
      store.getState().setIsLoading(loading)
    },
    [store]
  )

  if (isExited) {
    return null
  }

  return (
    <FlowContext.Provider
      value={{
        data,
        phases: enabledPhases,
        currentPhase,
        currentPhaseId,
        currentPhaseIndex,
        isFirstPhase,
        isLastPhase,
        nextPhase: goToNextPhase,
        previousPhase: goToPreviousPhase,
        goToPhase,
        updateData,
        completeFlow,
        resetFlow,
        canProceed,
        canGoBack,
        onClose: handleClose,
        setNextPhaseAction,
        isLoading: displayIsLoading,
        setIsLoading: setIsLoadingWrapper,
        recalculateCanProceed,
      }}>
      <ContentBox
        variant={contentBoxVariant}
        contentContainerClassName="gap-8 p-0 pb-6"
        className={cn(
          className,
          'transition-opacity duration-150',
          isInactive && 'pointer-events-none opacity-50'
        )}
        footerClassName="pointer-events-none"
        headerClassName="pb-0"
        header={
          <FlowHeader
            title={flowTitle}
            className={cn('px-6 pt-6', headerClassName)}
            totalPhases={enabledPhasesForProgress.length}
            currentPhase={Math.min(currentPhaseIndex, enabledPhasesForProgress.length - 1)}
            phaseTitle={currentPhase?.title}
            onReset={showResetButton && !isInactive ? resetFlow : undefined}
            onClose={showCloseButton && !isInactive && onClose ? handleClose : undefined}
          />
        }
        footer={
          footer ??
          (!usesCustomButtons && (
            <FlowNavigation
              nextDisabled={!canProceed}
              onPrevious={currentPhaseIndex > 0 ? goToPreviousPhase : undefined}
              onNext={goToNextPhase}
              nextButtonVariant={nextButtonVariantValue}
              isLoading={displayIsLoading}
              nextLabel={isLastPhaseForProgress ? completeLabelText : nextLabelText}
              previousLabel={previousLabelText}
              className={cn(
                isInBottomSheet && 'rounded-b-none',
                isInactive && 'pointer-events-none opacity-50'
              )}
            />
          ))
        }>
        <View className={cn('px-5 pt-6', contentClassName)}>{children}</View>
      </ContentBox>
    </FlowContext.Provider>
  )
}
